import React, { useEffect, useState } from "react";
import styles from "./JobsatRegenesys.module.css";
import { RotateCcw } from "lucide-react";

interface FilterData {
  country: string;
  category: string;
}

interface Props {
  filterFieldOne: FilterData[];
  onClickFilter: (selectedItem: FilterData) => void;
  data?: { title: string; description: string };
}

const JobsatRegenesys: React.FC<Props> = ({
  filterFieldOne = [],
  onClickFilter,
  data,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [filteredCategories, setFilteredCategories] = useState<string[]>([]);

  const handleResetFilter = () => {
    setSelectedCountry("");
    setSelectedCategory("");
    const allCategories = filterFieldOne.map((item) => item.category);
    const uniqueCategories = Array.from(new Set(allCategories));
    setFilteredCategories(uniqueCategories);
    onClickFilter({ country: "", category: "" });
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleResize = () => setIsMobile(window.innerWidth <= 767);
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      const categoriesForCountry = filterFieldOne
        .filter((item) => item.country === selectedCountry)
        .map((item) => item.category);

      const uniqueCategories = Array.from(new Set(categoriesForCountry));
      setFilteredCategories(uniqueCategories);
    } else {
      const allCategories = filterFieldOne.map((item) => item.category);
      const uniqueCategories = Array.from(new Set(allCategories));
      setFilteredCategories(uniqueCategories);
    }
  }, [selectedCountry, filterFieldOne]);

  // Handle Filter Submission
  const handleFilterSubmit = () => {
    const dataToSend = {
      country: selectedCountry,
      category: selectedCategory,
    };
    onClickFilter(dataToSend);
  };

  const formatText = (text?: string | null): string => {
    if (!text || typeof text !== "string") return "";
    return text
      .replace(/\/|-/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Extract unique countries
  const uniqueCountries = Array.from(
    new Set(filterFieldOne.map((item) => item.country || ""))
  ).filter(Boolean);

  return (
    <>
      <div className="container">
        <div className={styles.worldClassContainer}>
          <h2>{data?.title}</h2>
          <p className={styles.worldClassEduDesc}>{data?.description}</p>
        </div>
      </div>

      <div className={isMobile ? "" : "container"}>
        <div className={styles.filterContainer}>
          {/* Country Filter */}
          <div className={styles.filterItem}>
            <label className={styles.label}>Filter by Country</label>
            <select
              className={styles.select}
              value={selectedCountry}
              onChange={(e) => {
                setSelectedCountry(e.target.value);
                setSelectedCategory(""); // Reset category when country changes
              }}
            >
              <option value="" disabled>
                Select Country
              </option>
              {uniqueCountries.map((country, index) => (
                <option key={index} value={country}>
                  {formatText(country)}
                </option>
              ))}
            </select>
          </div>

          {/* Category Filter */}
          <div className={styles.filterItem}>
            <label className={styles.label}>Job Category</label>
            <select
              className={styles.select}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              disabled={!filteredCategories.length}
            >
              <option value="" disabled hidden>
                Select Job Category
              </option>
              {filteredCategories.filter(Boolean).map((category, index) => (
                <option key={index} value={category}>
                  {formatText(category)}
                </option>
              ))}
            </select>
          </div>

          {/* Submit Button */}
          <div className={styles.filterButtonMobile}>
            <button
              className={styles.filterButton}
              onClick={handleFilterSubmit}
              disabled={!selectedCountry && !selectedCategory}
            >
              Filter
            </button>
            {(selectedCountry || selectedCategory) && (
              <RotateCcw
                className={styles.resetButton}
                onClick={handleResetFilter}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default JobsatRegenesys;
